import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Xmark } from '@gravity-ui/icons';
import { Button, Icon, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { ApiError, granetApi } from 'client/api';
import { AliceAppActionButton } from 'client/blocks-redesign/AliceAppActionButton/AliceAppActionButton';
import { GranetCodeEditor } from 'client/blocks-redesign/grammar/GranetCodeEditor/GranetCodeEditor';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { useSettingsPageContext } from 'client/context/settingsPageContext';
import { Context as SnapshotContext } from 'client/context/snapshotContext';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { granetEntitiesCompletionProvider } from 'client/lib/monaco/languages/granet/completion';
import { GrammarValidationErrorPayload } from 'client/model/grammar';
import { AliceApp } from 'client/model/skill';
import * as keyset from 'i18n/IntentsPage';

import styles from './GrammarPageEntitiesSettings.module.scss';

interface Props {
    aliceApp: AliceApp;
    onClose: () => void;
    afterSave: () => void;
    setIsChanged: (isChanged: boolean) => void;
    disabled: boolean;
}
const GRAMMAR_TOAST = 'GRAMMAR_TOAST';
const SHOW_TOASTER_DELAY = 20000;

const t = i18n(keyset);

export const GrammarPageEntitiesSettings: React.FC<Props> = ({
    aliceApp,
    onClose,
    setIsChanged,
    afterSave,
    disabled,
}) => {
    const { refreshSnapshot } = useContext(SnapshotContext);
    const currentAliceAppContext = useContext(CurrentAliceAppContext);
    const { isDraft, isEditable } = useSettingsPageContext();
    const { add: addToast } = useToaster();

    const [validationError, setValidationError] = useState<GrammarValidationErrorPayload | undefined>(undefined);

    const initialEntities = isDraft ? aliceApp.draft.customEntities : aliceApp.customEntities;
    const [entitiesCode, setEntitiesCode] = useState(initialEntities ?? '');

    const onEntitiesChange = useCallback(
        (value: string) => {
            setEntitiesCode(value);

            setIsChanged(true);
        },
        [setIsChanged],
    );

    const afterLoad = useCallback(() => {
        refreshSnapshot();
        currentAliceAppContext && currentAliceAppContext.refreshAliceApp();
    }, [refreshSnapshot, currentAliceAppContext]);

    const [onSave, savingState] = useFetchingHandler({
        handler: granetApi.updateCustomEntities,
        throwErrors: true,
        afterLoad,
    });

    useEffect(() => {
        setValidationError(undefined);
    }, [entitiesCode, savingState]);

    const onSaveAction = useCallback(async () => {
        try {
            await onSave({ sourceText: entitiesCode, skillId: aliceApp.id }, aliceApp.channel);
            afterSave();
        } catch (e) {
            if (e instanceof ApiError && e.isGrammarValidationError()) {
                setValidationError(e.payload);
                addToast &&
                    addToast({
                        title: t('validation-error'),
                        theme: 'danger',
                        name: GRAMMAR_TOAST,
                        autoHiding: SHOW_TOASTER_DELAY,
                    });
            } else if (e instanceof ApiError && e.isCustomEntityInUseError()) {
                addToast &&
                    addToast({
                        title: t('entity-may-been-deleted-error'),
                        theme: 'danger',
                        name: GRAMMAR_TOAST,
                        autoHiding: SHOW_TOASTER_DELAY,
                    });
            } else if (e instanceof ApiError && e.payload?.errorCode === 'GRAMMAR_SIZE_LIMIT') {
                addToast &&
                    addToast({
                        title: t('size-exceed-error'),
                        theme: 'danger',
                        name: GRAMMAR_TOAST,
                        autoHiding: SHOW_TOASTER_DELAY,
                    });
            }
            throw e;
        }
    }, [addToast, aliceApp.id, aliceApp.channel, entitiesCode, afterSave, onSave]);

    const isSaving = savingState === 'loading';

    return (
        <div className={styles.grammarPageEntitiesSettings__container}>
            <header className={styles.grammarPageEntitiesSettings__header}>
                <h2 className={styles.grammarPageEntitiesSettings__title}>{t('entities-title')}</h2>
                <Button onClick={onClose} size="l" view="flat">
                    <Icon data={Xmark} />
                </Button>
            </header>
            <div className={styles.grammarPageEntitiesSettings__editor}>
                <GranetCodeEditor
                    onChange={(code) => onEntitiesChange(code)}
                    code={entitiesCode}
                    validationError={validationError}
                    onSave={onSaveAction}
                    readOnly={!isEditable || isSaving}
                    completionProvider={granetEntitiesCompletionProvider}
                />
            </div>
            <footer className={styles.grammarPageEntitiesSettings__footer}>
                <AliceAppActionButton
                    action={onSaveAction}
                    successLabel={t('changes-saved')}
                    size="l"
                    view="action"
                    disabled={isSaving || !isEditable || disabled}
                >
                    {t('button-save')}
                </AliceAppActionButton>
            </footer>
        </div>
    );
};
